import { useContext, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useAnalyticsContext } from 'app/shared/utils';
import { eventIsToday } from 'app/shared/utils';
import { calcEventState } from 'app/shared/utils/calcEventState';
import { Event, EventAttendee } from 'app/shared/typings';
import { AuthContext } from 'app/shared/context/Auth';
import { neighborhoodOrSecretLocation } from 'app/fan/utils';
import { VisaPartnershipCheckoutLogic } from 'app/fan/utils/checkout';
import {
  PAYMENT_METHOD_APPLE_PAY,
  PAYMENT_METHOD_GOOGLE_PAY,
  ThirdPartyPaymentMethodType,
} from 'app/fan/utils/checkout';

import { getEventVenueTagForKlaviyo } from './useGetEventTagsProps';

interface ButtonShownProps {
  isExpressCheckout: boolean;
  showApplePay: boolean;
  showGooglePay: boolean;
  event?: Event | null;
  eventAttendee?: EventAttendee | null;
  visaPartnershipCheckoutLogic?: VisaPartnershipCheckoutLogic;
}

export const useTrackVirtualWalletPaymentButtonShown = ({
  isExpressCheckout,
  showApplePay,
  showGooglePay,
  event,
  eventAttendee,
  visaPartnershipCheckoutLogic,
}: ButtonShownProps) => {
  const { trackAnalyticsEvent } = useAnalyticsContext();
  const { loggedIn, user: loggedInUser } = useContext(AuthContext);

  const visaPreSaleEligible = visaPartnershipCheckoutLogic?.visaPreSaleEligible;

  const trackVirtualWalletPaymentButtonShown = useMemo(() => {
    return function ({
      showApplePay,
      showGooglePay,
    }: {
      showApplePay: boolean;
      showGooglePay: boolean;
    }) {
      const paymentMethodType: ThirdPartyPaymentMethodType = showGooglePay
        ? PAYMENT_METHOD_GOOGLE_PAY
        : PAYMENT_METHOD_APPLE_PAY;

      const sharedArgs = {
        is_visa_validated: !!visaPreSaleEligible,
        checkout_id: eventAttendee ? eventAttendee.id : null,
        city_slug: event?.city?.cachedSlug,
        is_guest_checkout: !loggedIn,
        payment_method_type: paymentMethodType,
        apple_pay: showApplePay,
        google_pay: showGooglePay,
        user_id: loggedInUser?.id,
      };

      trackAnalyticsEvent(
        'Virtual Wallet Payment Button Shown',
        {
          ...sharedArgs,
          payment_method: paymentMethodType, // New property is payment_method_type, but leave this for historical data reasons
          checkout_version: 2,
          is_express_checkout: isExpressCheckout,
          credit_card: !isExpressCheckout, // TODO: change this logic we add express email/CC checkout
        },
        true,
        event?.id
      );

      isExpressCheckout &&
        trackAnalyticsEvent(
          'Express Checkout Displayed',
          {
            ...sharedArgs,
            credit_card: false, // TODO: Make dynamic when we add express email/CC checkout
          },
          true,
          event?.id
        );
    };
  }, [
    event,
    eventAttendee,
    loggedIn,
    trackAnalyticsEvent,
    isExpressCheckout,
    visaPreSaleEligible,
  ]);

  useEffect(() => {
    if (showGooglePay || showApplePay) {
      trackVirtualWalletPaymentButtonShown({
        showGooglePay,
        showApplePay,
      });
    }
  }, [showGooglePay, showApplePay, trackVirtualWalletPaymentButtonShown]);
};

interface ButtonClickedProps {
  isExpressCheckout: boolean;
  event?: Event | null;
  eventAttendee?: EventAttendee | null;
  visaPartnershipCheckoutLogic?: VisaPartnershipCheckoutLogic;
}

export const useTrackVirtualWalletPaymentStarted = ({
  isExpressCheckout,
  event,
  eventAttendee,
  visaPartnershipCheckoutLogic,
}: ButtonClickedProps) => {
  const { formatMessage } = useIntl();
  const { trackAnalyticsEvent } = useAnalyticsContext();
  const { loggedIn, user: loggedInUser } = useContext(AuthContext);

  const visaPreSaleEligible = visaPartnershipCheckoutLogic?.visaPreSaleEligible;

  return useMemo(() => {
    return function (paymentMethodType: ThirdPartyPaymentMethodType) {
      const sharedAnalyticsArgs = {
        checkout_id: eventAttendee ? eventAttendee.id : null,
        content_ids: event?.id ? [event.id] : [],
        payment_method_type: paymentMethodType,
        is_visa_validated: visaPreSaleEligible,
        city_slug: event?.city?.cachedSlug,
        is_guest_checkout: !loggedIn,
        user_id: loggedInUser?.id,
        user_has_tickets: !!eventAttendee?.id,
        product_id: event?.id, // Snapchat needs this, please do not remove
        email: loggedInUser?.email?.toLowerCase().trim(), // Snapchat needs this, please do not remove
        rebrand: true,
      };

      trackAnalyticsEvent('Virtual Wallet Payment Button Clicked', {
        ...sharedAnalyticsArgs,
        payment_method: paymentMethodType, // New property is payment_method_type, but leave this for historical data reasons
        checkout_version: 2,
        is_express_checkout: isExpressCheckout,
      });

      const eventState = calcEventState({
        event,
        attendeeState: eventAttendee?.currentState,
      });

      isExpressCheckout &&
        trackAnalyticsEvent('Checkout Started', {
          ...sharedAnalyticsArgs,
          is_express_checkout: true,
          currency: event?.city?.currencyCode?.toUpperCase(),
          event_owner_type: event?.eventOrganizedAs,
          user_state: eventState,
          event_is_today: event ? eventIsToday(event.startsAt) : true,
          city_title: event?.city?.title,
          neighborhood: event
            ? neighborhoodOrSecretLocation(event, formatMessage)
            : formatMessage({ id: 'shared.secretLocation' }),
          local_starts_at: event?.localStartsAt,
          venue_tag: event ? getEventVenueTagForKlaviyo(event) : undefined,
        });
    };
  }, [
    event,
    eventAttendee,
    isExpressCheckout,
    loggedIn,
    trackAnalyticsEvent,
    visaPreSaleEligible,
  ]);
};
